import React from "react";
import { Box, Typography, Container, Toolbar } from "@mui/material";
import HomeHeader from "../../../Components/HomeHeader";
import HomeFooter from "../../../Components/HomeFooter";
import { globalStyles } from "../../../Utils/Styles";
import { StyledDivider as Divider } from "../../../Components/LightDivider";

/**
 * Blitz – Terms of Service (Creators)
 * Last Updated: November 3, 2025
 *
 * This page mirrors the layout of the business-entity TOS while reflecting the
 * one-click Creator Terms & Conditions governing BlitzPay and general platform use.
 */
const TermsOfServiceCreators = () => {
  return (
    <Box
      sx={{
        backgroundColor: "#f5f5f5",
        width: "100%",
        color: "#000",
      }}
    >
      <HomeHeader />
      <Toolbar />

      {/* Main Content */}
      <Container
        sx={{
          ...globalStyles.wholeHeightMinusToolbar,
          paddingTop: 6,
          paddingBottom: 4,
        }}
      >
        {/* Heading */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: "bold", mb: 3 }}>
            Terms of Service for Creators
          </Typography>
          <Typography variant="body1" paragraph>
            Welcome to Blitz! These Terms of Service ("<strong>Creator Terms</strong>")
            govern your ("<strong>Creator</strong>", "<strong>you</strong>", or
            "<strong>your</strong>") access to and use of the Blitz platform and
            BlitzPay. By clicking “I Agree,” registering a payout preference, or
            otherwise using Blitz’s services, you acknowledge that you have read,
            understood, and agree to be bound by these Creator Terms, our Privacy
            Policy, and the Blitz Platform Terms of Service.
          </Typography>
        </Box>

        <Divider />

        {/* 1. Acceptance */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
            1. Acceptance
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>1.1 One-Time Click-Through.</strong> You must review and accept
            these Creator Terms each time you register a payout preference or elect
            BlitzPay on an invoice. Clicking “I Agree” creates a legally binding
            agreement between you and Blitz Systems Corp ("<strong>Blitz</strong>",
            "<strong>we</strong>", or "<strong>us</strong>").
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>1.2 Updates.</strong> We may modify these Creator Terms at any
            time. Revised terms will be posted on our website or presented at
            click-through. Continued use of the Services after changes become
            effective constitutes your acceptance of the updated terms.
          </Typography>
        </Box>

        <Divider />

        {/* 2. Definitions */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
            2. Definitions
          </Typography>
          <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
            <li>
              <strong>Invoice:</strong> A billing request you submit to your client
              ("<em>Brand</em>" or "<em>Client</em>").
            </li>
            <li>
              <strong>Receivable:</strong> Your right to collect payment on an
              Invoice.
            </li>
            <li>
              <strong>Advance:</strong> Funds Blitz pays you immediately in
              exchange for assignment of your Receivable.
            </li>
            <li>
              <strong>Factoring&nbsp;Fee:</strong> 5%–10% fee applied to the Invoice
              Amount and deducted from the Advance.
            </li>
            <li>
              <strong>Due&nbsp;Date:</strong> The date your client must pay the full
              Invoice amount under the original payment terms.
            </li>
            <li>
              <strong>Financial&nbsp;Health&nbsp;Score:</strong> Internal metric
              reflecting your payment history and risk profile on Blitz.
            </li>
            <li>
              <strong>Deliverables:</strong> Any creative output, content, copy,
              captions, hashtags, video, audio, stills, drafts, raw files, edits,
              derivatives, and other materials you create or furnish in connection
              with a campaign or order.
            </li>
            <li>
              <strong>Likeness:</strong> Your name, image, voice, signature, social
              handle, persona, biographical information, and other indicia of identity.
            </li>
            <li>
              <strong>Designated Platforms:</strong> Platforms specified by Blitz
              or the Client (e.g., Instagram, TikTok, YouTube, X/Twitter, Facebook,
              Twitch, websites, streaming services, OTT, paid media platforms).
            </li>
          </Typography>
        </Box>

        <Divider />

        {/* 3. Sale of Receivable */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
            3. Sale of Receivable
          </Typography>
          <Typography variant="body1" paragraph>
            By selecting BlitzPay and receiving an Advance, you irrevocably sell,
            transfer, and assign to Blitz all right, title, and interest in the
            Receivable and underlying Invoice. The Receivable becomes the sole
            property of Blitz.
          </Typography>
        </Box>

        <Divider />

        {/* 4. Payment & Factoring Fee */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
            4. Payment & Factoring Fee
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>4.1 Advance Calculation.</strong> You will receive an Advance
            equal to the Invoice Amount minus the applicable Factoring Fee.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>4.2 Fee Determination.</strong> The Factoring Fee is based on
            your Financial Health Score, invoice risk, and transaction history.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>4.3 Payout Methods.</strong> Advances are disbursed via your
            selected payout option (ACH, PayPal, Stripe, or wire transfer). Third-party
            processor fees may apply and are your responsibility.
          </Typography>
        </Box>

        <Divider />

        {/* 5. Unconditional Repayment */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
            5. Unconditional Repayment
          </Typography>
          <Typography variant="body1" paragraph>
            You unconditionally agree that Blitz owns the Receivable. You waive any
            right of set-off, counterclaim, defense, or dispute. If your client fails
            to pay, you remain solely liable to Blitz for the original Invoice Amount
            plus the Factoring Fee.
          </Typography>
        </Box>

        <Divider />

        {/* 6. Representations & Warranties */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
            6. Representations & Warranties
          </Typography>
          <Typography variant="body1" component="ul" sx={{ pl: 3 }}>
            <li>The Invoice is valid, undisputed, and due under your agreement with the Brand.</li>
            <li>No third party holds rights to the Receivable you assign to Blitz.</li>
            <li>You have fully performed the services or delivered the goods reflected by the Invoice.</li>
            <li>You will not amend, offset, or cancel the Invoice without Blitz’s prior written consent.</li>
            <li>You have and will obtain all necessary rights, licenses, clearances, and permissions to grant the transfers and licenses set forth herein.</li>
          </Typography>
        </Box>

        <Divider />

        {/* 7. Ownership & IP Transfer (NEW) */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
            7. Ownership & IP Transfer
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>7.1 Transfer to Blitz.</strong> Except for pre-existing materials
            owned by you or third parties (<em>Pre-Existing IP</em>) identified to Blitz
            in writing prior to creation, you hereby irrevocably assign and transfer to
            Blitz, for good and valuable consideration, all right, title, and interest
            worldwide in and to the Deliverables, including all intellectual property
            rights therein (copyrights and extensions/renewals, moral rights to the
            extent waivable, performance rights, rental/lending rights, and all rights
            of action for past, present, and future infringement).
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>7.2 Further Transfer to Client.</strong> Blitz may assign, license,
            or otherwise transfer any or all rights in the Deliverables to the Client
            and/or its designees without further approval or payment to you.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>7.3 Work-Made-For-Hire.</strong> To the maximum extent permitted
            by law, the Deliverables are specially commissioned works-made-for-hire for
            Blitz. If any Deliverable (or portion) is not deemed a work-made-for-hire,
            then it is assigned to Blitz under Section 7.1 at the moment of creation.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>7.4 Moral Rights.</strong> To the extent permitted by applicable
            law, you irrevocably waive and agree not to assert any moral rights or
            similar rights in the Deliverables against Blitz, the Client, or their
            licensees, successors, and assigns.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>7.5 Pre-Existing IP.</strong> If and only if you identify
            specific Pre-Existing IP in writing before use and obtain Blitz’s written
            consent, you grant to Blitz (and Blitz may further grant to Client) a
            perpetual, irrevocable, worldwide, royalty-free, fully paid-up,
            transferable, sublicensable license to use, reproduce, distribute,
            perform, display, adapt, modify, and create derivative works of such
            Pre-Existing IP solely as incorporated into or necessary to exploit the
            Deliverables.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>7.6 Third-Party Materials.</strong> You are responsible for
            securing all third-party clearances (e.g., music, footage, fonts, photos,
            collaborators) required for the Deliverables unless Blitz expressly agrees
            in writing to secure such rights.
          </Typography>
        </Box>

        <Divider />

        {/* 8. Usage Rights for Client (NEW) */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
            8. Usage Rights for Client
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>8.1 Client Usage.</strong> The Client (and its affiliates,
            licensees, partners, media buyers, and designees) may use, reproduce,
            distribute, perform, display, adapt, modify, edit, crop, caption,
            transcode, advertise, promote, and otherwise exploit the Deliverables,
            in whole or in part, across all media and methods of delivery now known
            or later developed, worldwide and in perpetuity, unless otherwise limited
            in a written campaign order.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>8.2 Likeness Rights.</strong> You grant Blitz and the Client a
            worldwide, perpetual, royalty-free, transferable, sublicensable license to
            use your Likeness in connection with the Deliverables and the Client’s or
            Blitz’s products and services (including paid advertising, whitelisting,
            boosting, dark posts, case studies, testimonials, editorial, and PR).
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>8.3 No Obligation to Use.</strong> Neither Blitz nor the Client is
            obligated to publish, credit, or continue using any Deliverable or your
            Likeness, and may choose not to feature you at any time and for any reason.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>8.4 Attribution.</strong> Any credit or attribution is at Blitz’s
            or the Client’s discretion unless otherwise required by platform tools or
            specified in a written campaign order.
          </Typography>
        </Box>

        <Divider />

        {/* 9. Publication Requirements (NEW) */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
            9. Publication Requirements
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>9.1 Posting Obligations.</strong> You will publish the specified
            content on the Designated Platforms in accordance with Blitz’s written
            requirements, which may include content format, minimum specs/quality,
            posting timelines, frequency, captions, hashtags, tags, links, calls to
            action, coupon codes, geotags, and usage of platform-native paid
            partnership or brand tools.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>9.2 Compliance.</strong> All posts must comply with applicable
            laws, platform rules, and industry standards (including FTC/ASA guidance
            on endorsements and disclosures, music licensing rules, and community
            guidelines). You are responsible for including clear and conspicuous
            disclosures (e.g., #ad, paid partnership tags) where required.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>9.3 Proof of Publication.</strong> Upon request, you will provide
            timely proof of publication, analytics, screenshots, post links, and
            access enabling whitelisting/boosting where applicable.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>9.4 Edits/Reshoots.</strong> If Deliverables do not meet stated
            requirements, you will make commercially reasonable edits, reshoots, or
            reposts in a timely manner at no additional cost unless otherwise agreed
            in writing.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>9.5 Breach Remedies.</strong> Failure to satisfy Publication
            Requirements is a material breach and may result in non-payment, chargebacks,
            setoffs against future amounts, and/or requirement to refund any Advance.
          </Typography>
        </Box>

        <Divider />

        {/* 10. KYC & Compliance (renumbered) */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
            10. KYC & Compliance
          </Typography>
          <Typography variant="body1" paragraph>
            You authorize Blitz to collect and verify identity, tax, and banking
            information (e.g., W-9, voided check, micro-deposits) and to share such
            information with third-party providers for the purpose of compliance with
            applicable anti-money-laundering, sanctions, and tax regulations.
          </Typography>
        </Box>

        <Divider />

        {/* 11. Dispute Resolution & Indemnification (renumbered) */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
            11. Dispute Resolution & Indemnification
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>11.1 Brand Disputes.</strong> If your client disputes an Invoice,
            you must promptly assist Blitz in resolving the dispute, including
            providing documentation and communications as requested.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>11.2 Indemnity.</strong> You agree to indemnify and hold Blitz
            harmless from any third-party claims, losses, or liabilities arising out of
            your representations, warranties, or non-compliance with these Creator
            Terms.
          </Typography>
        </Box>

        <Divider />

        {/* 12. Termination (renumbered) */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
            12. Termination
          </Typography>
          <Typography variant="body1" paragraph>
            Blitz may suspend or terminate your access to BlitzPay or the Platform if
            you breach these Creator Terms or if your Financial Health Score falls
            below required thresholds. Termination does not relieve you of any
            outstanding repayment obligations.
          </Typography>
        </Box>

        <Divider />

        {/* 13. Governing Law & Venue (renumbered) */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
            13. Governing Law & Venue
          </Typography>
          <Typography variant="body1" paragraph>
            These Creator Terms are governed by the laws of the State of Texas,
            without regard to conflict-of-law rules. Any dispute shall be
            exclusively resolved in the state or federal courts located in Houston,
            Texas, and you consent to personal jurisdiction in such courts.
          </Typography>
        </Box>

        <Divider />

        {/* 14. General Provisions (renumbered) */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
            14. General Provisions
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>14.1 Entire Agreement.</strong> These Creator Terms, the Blitz
            Platform Terms of Service, and any BlitzPay agreements constitute the
            entire agreement between the parties and supersede any prior
            understandings.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>14.2 Severability.</strong> If any provision is held invalid or
            unenforceable, the remaining provisions remain in full force and effect.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>14.3 Assignment.</strong> You may not assign these Creator Terms
            without Blitz’s prior written consent. Blitz may assign these Terms in
            whole or in part without notice.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>14.4 Waiver.</strong> Blitz’s failure to enforce any right or
            provision of these Terms is not a waiver of such right or provision.
          </Typography>
        </Box>

        <Divider />

        {/* 15. Contact Us (renumbered) */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
            15. Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have questions about these Creator Terms, please contact us at:
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Blitz Systems Corp</strong> <br />
            win@useblitz.co <br />
            1239 Cedar Post Line
          </Typography>
        </Box>

        <Divider />

        {/* 16. Acceptance of Terms (renumbered) */}
        <Box sx={{ my: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
            16. Acceptance of Terms
          </Typography>
          <Typography variant="body1" paragraph>
            By clicking “I Agree,” you confirm that you have read, understood, and
            accepted these Creator Terms & Conditions in their entirety. If you do
            not agree, do not use BlitzPay or the Blitz platform.
          </Typography>
          <Typography variant="body1" paragraph>
            <em>Last Updated: November 3, 2025</em>
          </Typography>
        </Box>
      </Container>

      <HomeFooter />
    </Box>
  );
};

export default TermsOfServiceCreators;
